import { useState } from 'react'
import { playgroundUrl } from './playgroundUrl'

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)
    const handleCopy = () => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    return (
        <button
            onClick={handleCopy}
            className="absolute top-3 right-3 text-text-muted hover:text-text transition-colors p-1 rounded cursor-pointer"
            title="Copy"
        >
            {copied ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 8.5l3 3 7-7" /></svg>
            ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="5" y="5" width="8" height="8" rx="1.5" /><path d="M3 11V3a2 2 0 012-2h8" /></svg>
            )}
        </button>
    )
}

function CodeLine({ command, copyText }: { command: string; copyText: string }) {
    return (
        <div className="relative">
            <pre
                className="font-mono text-[13px] rounded-lg p-4 pr-10 overflow-x-auto bg-bg"
                style={{ border: '1px solid var(--border)' }}
            >
                <span className="text-text-muted">$ </span>{command}
            </pre>
            <CopyButton text={copyText} />
        </div>
    )
}

const claudeCodeCmd = 'claude mcp add endiagram -- npx @endiagram/mcp'

const cursorJson = `{
  "mcpServers": {
    "endiagram": {
      "command": "npx",
      "args": ["-y", "@endiagram/mcp"]
    }
  }
}`

const codexToml = `[mcp_servers.endiagram]
command = "npx"
args = ["-y", "@endiagram/mcp"]`

// ─── Preview card headers — one per column of Get-Started ──────
//
// Both columns share the same elevated shell (rounded border + subtle
// accent tint) and both use a radial-accent hero above their content.
// Visual parity is the whole point: either path is equally legitimate,
// so the two cards must read at the same weight.
//
// Left (agent):     a mini "claude mcp add" terminal as the hero.
// Right (playground): a real 8-action structural render as the hero.

const GRADIENT_BG = `radial-gradient(circle at 50% 50%,
    color-mix(in srgb, var(--accent) 22%, transparent) 0%,
    color-mix(in srgb, var(--accent) 8%, transparent) 45%,
    transparent 80%)`

function AgentCardHeader() {
    // The terminal itself is the primary install block for Claude Code — the
    // duplicate h4-labelled CodeLine below used to carry the same command and
    // its own copy button. Folding the copy action into the hero terminal
    // kills the duplication without hiding the affordance.
    const [copied, setCopied] = useState(false)
    const handleCopy = () => {
        navigator.clipboard.writeText(claudeCodeCmd)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    return (
        <div
            className="relative flex items-center justify-center px-6 py-8"
            style={{
                backgroundImage: GRADIENT_BG,
                minHeight: '320px',
            }}
        >
            <div
                className="w-full max-w-sm rounded-lg overflow-hidden font-mono"
                style={{
                    border: '1px solid rgba(205, 214, 244, 0.08)',
                    background: '#1e1e2e',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
                }}
            >
                <div
                    className="flex items-center gap-1.5 px-3 py-2"
                    style={{
                        borderBottom: '1px solid rgba(205,214,244,0.06)',
                        background: '#181825',
                    }}
                >
                    <div className="w-2 h-2 rounded-full" style={{ background: '#ff5f57' }} />
                    <div className="w-2 h-2 rounded-full" style={{ background: '#febc2e' }} />
                    <div className="w-2 h-2 rounded-full" style={{ background: '#28c840' }} />
                    <span className="ml-1 text-[10px]" style={{ color: '#6c7086' }}>
                        claude code
                    </span>
                    <button
                        onClick={handleCopy}
                        className="ml-auto p-1 rounded transition-colors cursor-pointer"
                        style={{ color: copied ? '#a6e3a1' : '#6c7086' }}
                        aria-label={copied ? 'Copied' : 'Copy command'}
                        title={copied ? 'Copied!' : 'Copy command'}
                    >
                        {copied ? (
                            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M3 8.5l3 3 7-7" />
                            </svg>
                        ) : (
                            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="5" y="5" width="8" height="8" rx="1.5" />
                                <path d="M3 11V3a2 2 0 012-2h8" />
                            </svg>
                        )}
                    </button>
                </div>
                <div
                    className="px-4 py-4 text-[12.5px] leading-[1.7]"
                    style={{ color: '#cdd6f4' }}
                >
                    <span style={{ color: '#6c7086' }}>$ </span>
                    <span>{claudeCodeCmd}</span>
                </div>
            </div>
        </div>
    )
}

// Playground cover: a landscape diagram fills the background as a
// decorative wash, with the source and structure cards floating on
// top as rotated cutouts. Both cards carry real content — the source
// pane is the actual EN of the 8-action checkout, and the structure
// pane embeds /home-structure-preview.svg (a real render with
// structural overlays baked in), not hand-typed findings text.

function PreviewCardHeader() {
    return (
        <div
            className="relative overflow-hidden"
            style={{
                backgroundImage: GRADIENT_BG,
                minHeight: '440px',
            }}
        >
            {/* ── bg layer · landscape diagram, decorative ── */}
            <img
                src="/examples/hydraulic-cycle.svg"
                alt=""
                aria-hidden
                loading="lazy"
                className="absolute inset-0 w-full h-full pointer-events-none select-none"
                style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                    opacity: 0.42,
                    zIndex: 0,
                }}
            />

            {/* ── vignette · softens background so foreground pops ── */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(ellipse at center, transparent 15%,' +
                        ' color-mix(in srgb, var(--surface) 55%, transparent) 70%,' +
                        ' color-mix(in srgb, var(--surface) 80%, transparent) 100%)',
                    zIndex: 1,
                }}
            />

            {/* ── source cutout · top-left ── */}
            <div
                className="absolute font-mono rounded-lg overflow-hidden"
                style={{
                    top: '22px',
                    left: '18px',
                    width: 'clamp(160px, 46%, 240px)',
                    zIndex: 3,
                    border: '1px solid rgba(205, 214, 244, 0.14)',
                    background: '#1e1e2e',
                    boxShadow: '0 20px 44px rgba(0,0,0,0.65)',
                }}
            >
                <div
                    style={{
                        padding: '5px 10px',
                        fontSize: '9px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        color: '#6c7086',
                        background: '#181825',
                        borderBottom: '1px solid rgba(205,214,244,0.06)',
                    }}
                >
                    source
                </div>
                <pre
                    style={{
                        margin: 0,
                        padding: '8px 10px',
                        fontSize: '9px',
                        lineHeight: 1.55,
                        color: '#cdd6f4',
                    }}
                >
                    <span style={{ color: '#f5c2e7' }}>customer</span>{'\n'}
                    {'  '}<span style={{ color: '#89dceb' }}>do:</span> place order{'\n'}
                    {'  '}<span style={{ color: '#89dceb' }}>needs:</span> cart, payment{'\n'}
                    {'  '}<span style={{ color: '#89dceb' }}>yields:</span> order{'\n'}
                    {'  '}<span style={{ color: '#89dceb' }}>at:</span> web{'\n'}
                    {'\n'}
                    <span style={{ color: '#f5c2e7' }}>payment</span>{'\n'}
                    {'  '}<span style={{ color: '#89dceb' }}>do:</span> authorize{'\n'}
                    {'  '}<span style={{ color: '#89dceb' }}>needs:</span> order{'\n'}
                    {'  '}<span style={{ color: '#89dceb' }}>yields:</span> charged
                </pre>
            </div>

            {/* ── structure cutout · bottom-right · real panel screenshot ──
                topology-dark.png is the actual structure-panel view from
                the playground (restored from 360ea4b^). It's the real
                analysis UI — topology label, location chips, classified
                subsystems — NOT another diagram. The screenshot already
                carries its own "STRUCTURE" header, so the wrapper here
                is just a framed border, no topbar.

                The wrapper is intentionally shorter than the natural
                image height: the image renders at full width and the
                overflow is clipped at the bottom of the wrapper, so
                the topology label and first few subsystems are visible
                while the rest scrolls off — keeps the cover legible
                instead of being one giant panel. */}
            <div
                className="absolute rounded-lg overflow-hidden"
                style={{
                    top: '190px',
                    right: '18px',
                    width: 'clamp(170px, 44%, 235px)',
                    height: '230px',
                    zIndex: 3,
                    border: '1px solid var(--border-subtle)',
                    background: 'var(--bg)',
                    boxShadow: '0 20px 44px rgba(0,0,0,0.65)',
                }}
            >
                <img
                    src="/showcase/topology-dark.png"
                    alt="Structure panel — topology classification and subsystems"
                    loading="lazy"
                    style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                />
            </div>
        </div>
    )
}

const CARD_SHELL: React.CSSProperties = {
    border: '1px solid var(--border)',
    background: 'color-mix(in srgb, var(--accent) 3%, var(--surface))',
}

export function GetStarted() {
    return (
        <section id="get-started" className="py-24 px-6">
            <div className="max-w-6xl mx-auto">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">
                    Get Started
                </p>
                <h2 className="font-display text-3xl md:text-4xl font-bold mb-12 text-text">
                    Two ways in.
                </h2>

                <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-stretch">

                    {/* LEFT — add to your agent */}
                    <div className="flex flex-col">
                        <h3 className="font-display text-xl font-bold mb-2 text-text">
                            Add to your agent
                        </h3>
                        <p className="text-sm text-text-secondary leading-relaxed mb-8">
                            Install once in your coding agent. Six structural tools
                            become callable &mdash; we answer them from real math, so
                            the model doesn&rsquo;t have to guess.
                        </p>

                        <div
                            className="rounded-xl overflow-hidden flex flex-col h-full"
                            style={CARD_SHELL}
                        >
                            <AgentCardHeader />

                            <div className="flex flex-col gap-6 px-6 pt-5 pb-6 flex-1">
                                {/* Claude Code is already the hero above (terminal + copy
                                    button in AgentCardHeader) — no h4 section down here. */}

                                {/* Cursor */}
                                <div>
                                    <h4 className="text-sm font-bold font-mono text-text mb-1">Cursor</h4>
                                    <p className="text-xs text-text-muted mb-3">
                                        Add to <code className="text-accent">.cursor/mcp.json</code> in your project root, or <code className="text-accent">~/.cursor/mcp.json</code> for global.
                                    </p>
                                    <div className="relative">
                                        <pre
                                            className="font-mono text-[13px] rounded-lg p-4 pr-10 overflow-x-auto bg-bg leading-relaxed"
                                            style={{ border: '1px solid var(--border)' }}
                                        >
                                            <code>
                                                <span className="text-text-muted">{'{'}</span>{'\n'}
                                                {'  '}<span className="text-accent">"mcpServers"</span><span className="text-text-muted">: {'{'}</span>{'\n'}
                                                {'    '}<span className="text-accent">"endiagram"</span><span className="text-text-muted">: {'{'}</span>{'\n'}
                                                {'      '}<span className="text-accent">"command"</span><span className="text-text-muted">: </span><span className="text-warning">"npx"</span><span className="text-text-muted">,</span>{'\n'}
                                                {'      '}<span className="text-accent">"args"</span><span className="text-text-muted">: [</span><span className="text-warning">"-y"</span><span className="text-text-muted">, </span><span className="text-warning">"@endiagram/mcp"</span><span className="text-text-muted">]</span>{'\n'}
                                                {'    '}<span className="text-text-muted">{'}'}</span>{'\n'}
                                                {'  '}<span className="text-text-muted">{'}'}</span>{'\n'}
                                                <span className="text-text-muted">{'}'}</span>
                                            </code>
                                        </pre>
                                        <CopyButton text={cursorJson} />
                                    </div>
                                </div>

                                {/* Codex */}
                                <div>
                                    <h4 className="text-sm font-bold font-mono text-text mb-1">Codex</h4>
                                    <p className="text-xs text-text-muted mb-3">
                                        Add to <code className="text-accent">~/.codex/config.toml</code> or <code className="text-accent">.codex/config.toml</code> in your project root.
                                    </p>
                                    <div className="relative">
                                        <pre
                                            className="font-mono text-[13px] rounded-lg p-4 pr-10 overflow-x-auto bg-bg leading-relaxed"
                                            style={{ border: '1px solid var(--border)' }}
                                        >
                                            <code>
                                                <span className="text-text-muted">[</span><span className="text-accent">mcp_servers.endiagram</span><span className="text-text-muted">]</span>{'\n'}
                                                <span className="text-accent">command</span><span className="text-text-muted"> = </span><span className="text-warning">"npx"</span>{'\n'}
                                                <span className="text-accent">args</span><span className="text-text-muted"> = [</span><span className="text-warning">"-y"</span><span className="text-text-muted">, </span><span className="text-warning">"@endiagram/mcp"</span><span className="text-text-muted">]</span>
                                            </code>
                                        </pre>
                                        <CopyButton text={codexToml} />
                                    </div>
                                </div>

                                {/* HTTP alternative */}
                                <div className="pt-5 mt-auto" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                    <p className="text-xs text-text-muted mb-3 font-mono">
                                        No Node.js? Use HTTP directly &mdash; any MCP client.
                                    </p>
                                    <CodeLine
                                        command="https://api.endiagram.com/mcp"
                                        copyText="https://api.endiagram.com/mcp"
                                    />
                                </div>

                                <p className="text-[11px] text-text-muted text-center">
                                    Claude Desktop, Windsurf, Smithery &mdash;{' '}
                                    <a
                                        href="https://github.com/dushyant30suthar/endiagram-mcp#readme"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-accent hover:underline"
                                    >
                                        full setup guide
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — open the playground */}
                    <div className="mt-16 lg:mt-0 flex flex-col">
                        <h3 className="font-display text-xl font-bold mb-2 text-text">
                            Open the playground
                        </h3>
                        <p className="text-sm text-text-secondary leading-relaxed mb-8">
                            Not ready to wire it into an agent yet? The same engine runs in the
                            browser. Paste any EN, toggle overlays, try themes, export SVG/PNG.
                        </p>

                        <div
                            className="rounded-xl overflow-hidden flex flex-col h-full"
                            style={CARD_SHELL}
                        >
                            <PreviewCardHeader />

                            <div className="flex flex-col gap-6 px-6 pt-6 pb-6 flex-1">
                                {/* Plain vertical list — flex-1 + justify-between
                                    spreads the four items across the card so the
                                    right column visually matches the left agent
                                    card's stacked config blocks. No dividers, no
                                    numbering — the bold name carries the rhythm. */}
                                <ul className="flex flex-col flex-1 justify-between gap-5">
                                    <li>
                                        <p className="text-sm font-bold text-text mb-1.5">Topology-aware layout</p>
                                        <p className="text-xs text-text-secondary leading-relaxed">
                                            Pipeline, fork-join, state machine, complex &mdash; the
                                            renderer picks a layout that matches the shape the
                                            engine detects, instead of one default for everything.
                                        </p>
                                    </li>
                                    <li>
                                        <p className="text-sm font-bold text-text mb-1.5">Auto-discovered subsystems</p>
                                        <p className="text-xs text-text-secondary leading-relaxed">
                                            Paste a hundred-action system and the canvas partitions
                                            it into its true subsystems &mdash; no manual grouping,
                                            no tags to author.
                                        </p>
                                    </li>
                                    <li>
                                        <p className="text-sm font-bold text-text mb-1.5">Live structural findings</p>
                                        <p className="text-xs text-text-secondary leading-relaxed">
                                            Bridges, cycles, siphons, invariants, bottlenecks &mdash;
                                            listed beside the source and pinned to the exact
                                            nodes they describe. Edit the system; they follow.
                                        </p>
                                    </li>
                                    <li>
                                        <p className="text-sm font-bold text-text mb-1.5">Themes, layers &amp; export</p>
                                        <p className="text-xs text-text-secondary leading-relaxed">
                                            Curated palettes or seed a custom one from a hex.
                                            Toggle subsystem, cycle, or hub overlays; download
                                            any state as SVG or PNG.
                                        </p>
                                    </li>
                                </ul>

                                <a
                                    href={playgroundUrl}
                                    className="mt-auto inline-flex items-center justify-center bg-accent text-bg font-bold px-6 py-3 rounded-full text-sm hover:opacity-90 transition-opacity"
                                >
                                    Open Playground &rarr;
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
